/** Admin: courses, batches (trainer offers, class schedule) and assessments. */
import { api } from '../../core/api.js';
import { config } from '../../core/config.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { fmtDate, inr, toPaise, weekdayNames } from '../../core/format.js';
import { CATEGORIES, WEEKDAYS } from '../../core/constants.js';
import { navigate } from '../../core/router.js';
import { currentView, reload } from '../../app/shell.js';
import { backLink, banner, empty, ic, pageHead, statusTag, table, tag } from '../../ui/components.js';
import { field, inrField, selectField, tagField } from '../../ui/forms.js';
import { closeModal, confirmBox, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';
import { filterSelect, queryOf } from './shared.js';

const COURSE_STATUS = [['draft', 'Draft, hidden'], ['active', 'Active, on sale'], ['archived', 'Archived']];

/** What a student can do with this course right now, in plain words (so admin never wonders "why can't they see it?"). */
export function courseReadiness(c) {
  if (c.status === 'archived') return { kind: 'n', short: 'Archived', long: 'Archived: students cannot see it.' };
  if (c.status !== 'active') return { kind: 'wn', short: 'Hidden (draft)', long: 'Draft: students cannot see this course yet. Set Status to Active.' };
  if (!c.open_batches) return { kind: 'wn', short: 'Visible, enrolment closed', long: 'Students can see this course, but nobody can pay until a batch has a start date.' };
  return { kind: 'ok', short: `Enrolment open, starts ${fmtDate(c.next_start)}`, long: `Enrolment is open. The next batch starts ${fmtDate(c.next_start)}.` };
}

const weekdayPicker = () => html`<fieldset class="field" style="border:0;padding:0"><legend class="sm b">Class days</legend><div class="row">${WEEKDAYS.map(([n, label]) => html`<label class="row sm" style="gap:4px"><input type="checkbox" name="wd${n}"> ${label}</label>`)}</div></fieldset>`;
const pickedDays = (v) => WEEKDAYS.map(([n]) => n).filter((n) => v[`wd${n}`]);

/* ---------- courses ---------- */

const courses = {
  path: '/admin/courses',
  roles: ['admin'],
  title: 'Courses',
  load: (ctx) => api.get('/admin/courses', { signal: ctx.signal }),
  view(d) {
    return html`${pageHead('Courses', `Price is per month. Students are billed monthly${config.gstRegistered ? ' with GST added' : ''}.`, html`<a class="btn" href="/admin/courses/new">${ic('plus')} New course</a>`)}
      ${d.courses.length
        ? table(['Course', 'Category', 'Price / month', 'Classes', 'Status', 'Students can', ''], d.courses.map((c) => {
          const r = courseReadiness(c);
          return [html`<b>${c.title}</b>${c.trial_enabled ? html` ${tag('Free trial', 'n')}` : ''}`, c.category, c.price_paise === 0 ? 'Free' : inr(c.price_paise), c.total_classes, statusTag(c.status), tag(r.short, r.kind), html`<a class="btn ghost sm" href="/admin/courses/${c.id}">Edit</a>`];
        }))
        : empty('book', 'No courses yet', 'Create the first course.', html`<a class="btn" href="/admin/courses/new">New course</a>`)}`;
  },
};

function courseForm(c) {
  return html`<form data-submit="course.save" data-id="${c?.id || ''}" novalidate>
    ${field('co-title', 'Title', { name: 'title', val: c?.title, minlen: 3, maxlength: 160 })}
    <div class="fgrid">${field('co-cat', 'Category', { name: 'category', opts: CATEGORIES, val: c?.category })}${selectField('co-status', 'Status', COURSE_STATUS, { name: 'status', val: c?.status || 'draft', help: 'Students only see Active courses.' })}
      ${inrField('co-price', 'Price per month (before GST)', { name: 'price', number: true, val: c ? c.price_paise / 100 : '', help: 'Enter 0 for a free course or webinar. Students then register with one click and nobody pays.' })}${field('co-classes', 'Total classes', { type: 'number', name: 'total_classes', min: 1, max: 200, val: c?.total_classes ?? 24 })}
      ${selectField('co-trial', 'Free trial class', [['false', 'No trial class'], ['true', 'Yes, offer one free trial class']], { name: 'trial_enabled', val: String(c?.trial_enabled ?? false), help: 'Students can book one free trial before paying. It uses an upcoming class of this course.' })}</div>
    ${field('co-desc', 'Description', { name: 'description', rows: 4, req: false, max: 5000, val: c?.description })}
    ${tagField('co-skills', 'Skills students earn', { name: 'skills', preset: c?.skills || [], help: 'These power candidate matching, so use the same names companies ask for.' })}
    ${field('co-curr', 'Curriculum', { name: 'curriculum', rows: 8, req: false, max: 12000, val: (c?.curriculum || []).join('\n'), help: 'One topic per line, up to 60.' })}
    ${c ? '' : html`<div class="card flat mt-s"><h3 style="margin-top:0">Start date and first batch <span class="mu sm">(optional)</span></h3>
      <p class="sm mu">Set a start date and students can pay and enrol straight away. The classes are created for you and the course is made Active. Leave it empty to add batches later.</p>
      <div class="fgrid">${field('co-first', 'Start date (first class)', { name: 'first_day', type: 'date', req: false })}${field('co-time', 'Class start time (India time)', { name: 'start_time', type: 'time', val: '18:00', req: false })}
        ${field('co-seats', 'Seats', { name: 'capacity', type: 'number', min: 1, max: 500, val: 30, req: false })}</div>${weekdayPicker()}</div>`}
    <div class="row mt-s" style="gap:10px;flex-wrap:wrap"><button class="btn" type="submit">${c ? 'Save changes' : 'Create course'}</button>
      ${c ? html`<button class="btn ghost danger" type="button" data-act="course.delete" data-id="${c.id}" data-title="${c.title}">Delete course</button>` : ''}</div>
    ${c ? html`<p class="sm mu mt-s">Delete removes a course that was never sold, together with its batches and timetable. A course with payments, students or classes already taught is kept for the records: set its status to Archived instead.</p>` : ''}</form>`;
}

const courseEdit = {
  path: '/admin/courses/:id',
  roles: ['admin'],
  nav: 'courses',
  title: (d) => (d?.course ? d.course.title : 'New course'),
  async load(ctx) {
    if (ctx.params.id === 'new') return { course: null };
    const { courses: list } = await api.get('/admin/courses', { signal: ctx.signal });
    const course = list.find((c) => c.id === Number(ctx.params.id));
    if (!course) throw new Error('Course not found.');
    return { course };
  },
  view: ({ course }) => {
    const r = course ? courseReadiness(course) : null;
    return html`${backLink('/admin/courses', 'Courses')}<div class="card" style="max-width:760px"><h2>${course ? 'Edit course' : 'New course'}</h2>
      ${r ? banner(r.long, r.kind === 'ok' ? 'good' : 'wn', r.kind === 'ok' ? 'check' : 'info') : ''}
      ${r && course.status === 'active' && !course.open_batches ? html`<p class="sm"><a href="/admin/batches?course_id=${course.id}">Add a batch with a start date</a></p>` : ''}${courseForm(course)}</div>`;
  },
};

register({
  'course.delete': async (el) => {
    const sure = await confirmBox({ title: `Delete "${el.dataset.title}"?`, text: 'This permanently removes the course, its batches and its timetable. It cannot be undone.', label: 'Delete course', danger: true, cancel: 'Keep it' });
    if (!sure) return;
    try {
      await api.delete(`/admin/courses/${el.dataset.id}`);
      toast('Course deleted', 'check');
      await navigate('/admin/courses');
    } catch (error) {
      toast(error.message, 'alert');
    }
  },
  'course.save': (form) => submitForm(form, async (v) => {
    const price = toPaise(v.price);
    if (!Number.isFinite(price) || (price !== 0 && price < 100)) throw new Error('Price must be at least ₹1, or 0 for a free course or webinar.');
    const body = {
      title: v.title, category: v.category, description: v.description, skills: v.skills, price_paise: price, total_classes: Number(v.total_classes), status: v.status,
      trial_enabled: v.trial_enabled === 'true', curriculum: v.curriculum.split('\n').map((x) => x.trim()).filter(Boolean),
    };
    const firstDay = form.dataset.id ? '' : v.first_day;
    const weekdays = pickedDays(v);
    if (firstDay) {
      if (!weekdays.length) throw new Error('Choose the class days for the first batch.');
      body.status = 'active';
    }
    if (form.dataset.id) {
      await api.patch(`/admin/courses/${form.dataset.id}`, { body });
      toast('Course saved', 'check');
    } else {
      const { course } = await api.post('/admin/courses', { body });
      if (firstDay) {
        try {
          await api.post('/admin/batches', { body: { course_id: course.id, name: 'B1', weekdays, start_time: v.start_time || '18:00', capacity: Number(v.capacity) || 30, first_day: firstDay } });
          toast('Course created. Students can now pay and enrol.', 'check');
        } catch (error) {
          toast(`Course saved, but the first batch was not created: ${error.message}`, 'alert');
          await navigate(`/admin/courses/${course.id}`);
          return;
        }
      } else {
        toast('Course saved. Add a batch with a start date so students can enrol.', 'check');
      }
    }
    await navigate('/admin/courses');
  }, { onError: (error) => toast(error.message, 'alert') }),
});

/* ---------- batches ---------- */

const batches = {
  path: '/admin/batches',
  roles: ['admin'],
  title: 'Batches',
  async load(ctx) {
    const query = queryOf(ctx);
    const [b, c, t] = await Promise.all([
      api.get('/admin/batches', { signal: ctx.signal, query: query.course_id ? { course_id: query.course_id } : {} }),
      api.get('/admin/courses', { signal: ctx.signal }),
      api.get('/admin/users', { signal: ctx.signal, query: { role: 'professional', verification: 'verified', status: 'active', limit: 100 } }),
    ]);
    return { batches: b.batches, courses: c.courses, trainers: t.users, query };
  },
  view(d) {
    const course = (id) => d.courses.find((c) => c.id === id)?.title || `Course ${id}`;
    const enrolmentState = (b) => {
      if (b.status === 'closed') return tag('Closed', 'n');
      if (!b.starts_on) return tag('Closed: set a start date', 'wn');
      if (b.seats_left <= 0) return tag('Full', 'wn');
      return tag(b.enrolment_open ? 'Open' : 'Closed', b.enrolment_open ? 'ok' : 'n');
    };
    const trainerState = (b) => (b.trainer_id ? html`${b.trainer_name || `User ${b.trainer_id}`} ${statusTag(b.trainer_status)}` : tag('No trainer', 'wn'));
    return html`${pageHead('Batches', 'A batch is a group of students on a weekly timetable with one trainer.', html`<button type="button" class="btn" data-act="batch.new">${ic('plus')} New batch</button>`)}
      <div class="row mb">${filterSelect('Course', 'course_id', d.courses.map((c) => [c.id, c.title]), d.query.course_id)}</div>
      ${d.batches.length
        ? table(['Course', 'Batch', 'Timetable', 'Starts', 'Trainer', 'Seats left', 'Pay / class', 'Enrolment', ''], d.batches.map((b) => [course(b.course_id), html`<b>${b.name}</b>`, html`${b.schedule || weekdayNames(b.weekdays)}<div class="xs mu">${b.class_minutes} min each</div>`, b.starts_on ? fmtDate(b.starts_on) : tag('Not set', 'wn'), trainerState(b), `${b.seats_left} of ${b.capacity}`, inr(b.pay_per_class_paise), enrolmentState(b),
          html`<div class="row" style="min-width:165px">${b.trainer_status === 'accepted' && b.status !== 'closed' ? html`<button type="button" class="btn sm" data-act="batch.now" data-id="${b.id}" data-name="${b.name}">${ic('play')} Class now</button>` : ''}<button type="button" class="btn ghost sm" data-act="batch.trainer" data-id="${b.id}" data-name="${b.name}" data-pay="${b.pay_per_class_paise}">Trainer</button>
            ${b.starts_on ? '' : html`<button type="button" class="btn sm" data-act="batch.sessions" data-id="${b.id}" data-name="${b.name}">Set start date</button>`}
            <button type="button" class="btn ghost sm" data-act="batch.edit" data-id="${b.id}" data-name="${b.name}" data-capacity="${b.capacity}" data-pay="${b.pay_per_class_paise}" data-status="${b.status}" data-note="${b.note || ''}" data-minutes="${b.class_minutes}">Edit</button></div>`]))
        : empty('cal', 'No batches', 'Create a batch for a course, then offer it to a trainer.')}`;
  },
};

const trainerOptions = (list) => list.map((t) => [t.id, `${t.name} (${t.email})`]);
const currentData = () => currentView().data;

register({
  'batch.new': () => {
    const d = currentData();
    openModal({
      title: 'New batch',
      wide: true,
      body: html`<form id="batch-form" data-submit="batch.create" novalidate>
        <div class="fgrid">${selectField('bn-course', 'Course', d.courses.filter((c) => c.status !== 'archived').map((c) => [c.id, c.title]), { name: 'course_id', placeholder: 'Choose a course' })}${field('bn-name', 'Batch name', { name: 'name', maxlength: 20, ph: 'B1' })}
        ${field('bn-time', 'Class start time (India time)', { name: 'start_time', type: 'time', val: '18:00' })}${field('bn-cap', 'Seats', { name: 'capacity', type: 'number', min: 1, max: 500, val: 30 })}
        ${inrField('bn-pay', 'Trainer pay per class', { name: 'pay', number: true, val: 0 })}${selectField('bn-trainer', 'Offer to trainer (optional)', trainerOptions(d.trainers), { name: 'trainer_id', req: false, placeholder: 'Decide later' })}
        ${field('bn-first', 'Start date (first class)', { name: 'first_day', type: 'date', req: false, help: 'Set it and students can pay and enrol; the classes are created for you.' })}${field('bn-classes', 'Number of classes', { name: 'classes', type: 'number', min: 1, max: 200, req: false, help: 'Leave empty to use the course total.' })}</div>
        ${weekdayPicker()}${field('bn-mins', 'Class length (minutes)', { name: 'class_minutes', type: 'number', min: 15, max: 240, val: 45 })}${field('bn-note', 'Batch note for students', { name: 'note', rows: 3, req: false, max: 500, ph: 'Completion certificate is provided only after the 3-month course.' })}</form>`,
      foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="batch-form" class="btn">Create batch</button>`,
    });
  },
  'batch.create': (form) => submitForm(form, async (v) => {
    const weekdays = pickedDays(v);
    if (!weekdays.length) throw new Error('Choose at least one class day.');
    const pay = toPaise(v.pay || '0');
    const body = { course_id: Number(v.course_id), name: v.name, weekdays, start_time: v.start_time, capacity: Number(v.capacity), pay_per_class_paise: Number.isFinite(pay) ? pay : 0, note: v.note || '', class_minutes: Number(v.class_minutes) || 45 };
    if (v.trainer_id) body.trainer_id = Number(v.trainer_id);
    if (v.first_day) {
      body.first_day = v.first_day;
      if (v.classes) body.classes = Number(v.classes);
    }
    const { classes_created: created, warnings = [] } = await api.post('/admin/batches', { body });
    closeModal(false);
    if (warnings.length) toast(warnings[0], 'alert');
    else toast(created ? `Batch created with ${created} classes. Students can now pay and enrol.` : 'Batch created. Set a start date so students can enrol.', 'check');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),

  'batch.trainer': (el) => {
    const d = currentData();
    openModal({
      title: `Trainer for batch ${el.dataset.name}`,
      body: html`<form id="trainer-form" data-submit="batch.trainer.send" data-id="${el.dataset.id}" novalidate>
        ${selectField('bt-trainer', 'Verified freelancer', trainerOptions(d.trainers), { name: 'trainer_id', placeholder: 'Choose a trainer' })}
        ${inrField('bt-pay', 'Pay per class', { name: 'pay', number: true, val: Number(el.dataset.pay) / 100 })}</form><p class="note">The freelancer gets an offer and must accept. Only verified, active freelancers can be offered a batch.</p>`,
      foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="trainer-form" class="btn">Send offer</button>`,
    });
  },
  'batch.trainer.send': (form) => submitForm(form, async ({ trainer_id: trainer, pay }) => {
    const paise = toPaise(pay || '0');
    const { warnings = [] } = await api.post(`/admin/batches/${form.dataset.id}/trainer`, { body: { trainer_id: Number(trainer), pay_per_class_paise: Number.isFinite(paise) ? paise : 0 } });
    closeModal(false);
    toast(warnings.length ? `Offer sent. Note: ${warnings[0]}` : 'Offer sent to the trainer', warnings.length ? 'alert' : 'send');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),

  'batch.now': (el) => openModal({
    title: `Class now for batch ${el.dataset.name}`,
    body: html`<form id="now-form" data-submit="batch.now.send" data-id="${el.dataset.id}" novalidate>
      ${field('cn-title', 'Class title', { name: 'title', req: false, maxlength: 200, ph: 'Doubt clearing session' })}${field('cn-mins', 'Length (minutes)', { name: 'duration_min', type: 'number', min: 15, max: 240, val: 45 })}</form>
      <p class="note">This adds one extra class that opens straight away. The trainer and students are notified. The trainer starts it, and it counts toward their pay per class.</p>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="now-form" class="btn">Create class</button>`,
  }),
  'batch.now.send': (form) => submitForm(form, async ({ title, duration_min: minutes }) => {
    const { number } = await api.post(`/admin/batches/${form.dataset.id}/class-now`, { body: { title: title || '', duration_min: Number(minutes) } });
    closeModal(false);
    toast(`Class ${number} is open. The trainer can start it now.`, 'check');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),

  'batch.sessions': (el) => openModal({
    title: `Start date for batch ${el.dataset.name}`,
    body: html`<form id="sessions-form" data-submit="batch.sessions.send" data-id="${el.dataset.id}" novalidate>
      ${field('bs-first', 'Start date (first class)', { name: 'first_day', type: 'date', help: 'Once set, students can pay and enrol.' })}${field('bs-count', 'Number of classes', { name: 'count', type: 'number', min: 1, max: 200, req: false, help: 'Leave empty to use the course total.' })}</form>
      <p class="note">Classes are created on the batch weekdays at its start time, India time. Students and the trainer see them straight away.</p>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="sessions-form" class="btn">Set start date</button>`,
  }),
  'batch.sessions.send': (form) => submitForm(form, async ({ first_day: first, count }) => {
    const body = { first_day: first };
    if (count) body.count = Number(count);
    const result = await api.post(`/admin/batches/${form.dataset.id}/sessions`, { body });
    closeModal(false);
    toast(`${result.created} classes scheduled, from ${fmtDate(result.first)} to ${fmtDate(result.last)}`, 'cal');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),

  'batch.edit': (el) => openModal({
    title: `Edit batch ${el.dataset.name}`,
    body: html`<form id="bedit-form" data-submit="batch.edit.send" data-id="${el.dataset.id}" novalidate><div class="fgrid">
      ${field('be-name', 'Batch name', { name: 'name', val: el.dataset.name, maxlength: 20 })}${field('be-cap', 'Seats', { name: 'capacity', type: 'number', min: 1, max: 500, val: el.dataset.capacity })}
      ${inrField('be-pay', 'Trainer pay per class', { name: 'pay', number: true, val: Number(el.dataset.pay) / 100 })}${selectField('be-status', 'Status', [['open', 'Open for enrolment'], ['running', 'Running'], ['closed', 'Closed']], { name: 'status', val: el.dataset.status })}</div>
      ${field('be-mins', 'Class length (minutes)', { name: 'class_minutes', type: 'number', min: 15, max: 240, val: el.dataset.minutes, help: 'Applies to classes not yet held.' })}
      ${field('be-note', 'Batch note for students', { name: 'note', rows: 3, req: false, max: 500, val: el.dataset.note, ph: 'Completion certificate is provided only after the 3-month course.', help: 'Students see this on the course page and class page, and are notified when it changes.' })}</form>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="bedit-form" class="btn">Save</button>`,
  }),
  'batch.edit.send': (form) => submitForm(form, async (v) => {
    const pay = toPaise(v.pay || '0');
    await api.patch(`/admin/batches/${form.dataset.id}`, { body: { name: v.name, capacity: Number(v.capacity), pay_per_class_paise: Number.isFinite(pay) ? pay : 0, status: v.status, note: v.note || '', class_minutes: Number(v.class_minutes) } });
    closeModal(false);
    toast('Batch updated', 'check');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
});

/* ---------- assessments ---------- */

/** "Question?\n* right answer\n- wrong\n- wrong" blocks separated by a blank line  <->  API questions. */
export function parseQuestions(text) {
  const blocks = text.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  return blocks.map((block, index) => {
    const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
    const q = lines[0];
    const options = [];
    let answer = -1;
    for (const line of lines.slice(1)) {
      const mark = line[0];
      if (mark !== '*' && mark !== '-') throw new Error(`Question ${index + 1}: start each option with "-" or, for the right answer, "*".`);
      if (mark === '*') {
        if (answer !== -1) throw new Error(`Question ${index + 1}: only one option can be marked right.`);
        answer = options.length;
      }
      options.push(line.slice(1).trim());
    }
    if (options.length < 2 || options.length > 6) throw new Error(`Question ${index + 1}: give 2 to 6 options.`);
    if (answer === -1) throw new Error(`Question ${index + 1}: mark the right option with "*".`);
    return { q, options, answer };
  });
}

export const formatQuestions = (questions) => questions.map((x) => [x.q, ...x.options.map((o, i) => `${i === x.answer ? '*' : '-'} ${o}`)].join('\n')).join('\n\n');

const assessments = {
  path: '/admin/assessments',
  roles: ['admin'],
  title: 'Assessments',
  async load(ctx) {
    const [a, c] = await Promise.all([api.get('/admin/assessments', { signal: ctx.signal }), api.get('/admin/courses', { signal: ctx.signal })]);
    return { assessments: a.assessments, courses: c.courses };
  },
  view(d) {
    const title = (id) => d.courses.find((c) => c.id === id)?.title || `Course ${id}`;
    return html`${pageHead('Assessments', 'Answer keys live only here. Students never receive them.', html`<a class="btn" href="/admin/assessments/new">${ic('plus')} New assessment</a>`)}
      ${d.assessments.length
        ? table(['Course', 'Assessment', 'Type', 'Questions', 'Pass mark', 'Status', ''], d.assessments.map((a) => [title(a.course_id), html`<b>${a.title}</b>`, a.kind === 'final' ? tag('Final', 'wn') : tag('Weekly'), a.questions.length, `${a.pass_percent}%`, a.active ? tag('Active', 'ok') : tag('Hidden', 'n'), html`<a class="btn ghost sm" href="/admin/assessments/${a.id}">Edit</a>`]))
        : empty('clip', 'No assessments', 'Add weekly quizzes and a final assessment to each course.')}`;
  },
};

const assessmentEdit = {
  path: '/admin/assessments/:id',
  roles: ['admin'],
  nav: 'assessments',
  title: (d) => (d?.assessment ? d.assessment.title : 'New assessment'),
  async load(ctx) {
    const [c, a] = await Promise.all([api.get('/admin/courses', { signal: ctx.signal }), ctx.params.id === 'new' ? Promise.resolve(null) : api.get('/admin/assessments', { signal: ctx.signal })]);
    const assessment = a ? a.assessments.find((x) => x.id === Number(ctx.params.id)) : null;
    if (a && !assessment) throw new Error('Assessment not found.');
    return { courses: c.courses, assessment };
  },
  view({ courses: list, assessment: a }) {
    return html`${backLink('/admin/assessments', 'Assessments')}<div class="card" style="max-width:800px"><h2>${a ? 'Edit assessment' : 'New assessment'}</h2>
      <form data-submit="assessment.save" data-id="${a?.id || ''}" novalidate>
        <div class="fgrid">${selectField('as-course', 'Course', list.map((c) => [c.id, c.title]), { name: 'course_id', val: a?.course_id, placeholder: 'Choose a course' })}${field('as-title', 'Title', { name: 'title', val: a?.title, minlen: 2, maxlength: 160 })}
        ${selectField('as-kind', 'Type', [['weekly', 'Weekly quiz'], ['final', 'Final assessment']], { name: 'kind', val: a?.kind || 'weekly' })}${field('as-pass', 'Pass mark (%)', { name: 'pass_percent', type: 'number', min: 1, max: 100, val: a?.pass_percent ?? 75 })}
        ${field('as-pos', 'Order in course', { name: 'position', type: 'number', min: 0, max: 1000, val: a?.position ?? 0 })}${selectField('as-active', 'Visible to students', [['true', 'Yes'], ['false', 'No']], { name: 'active', val: String(a?.active ?? true) })}</div>
        ${field('as-q', 'Questions', { name: 'questions', rows: 14, max: 40000, val: a ? formatQuestions(a.questions) : '', ph: 'Which HTTP method is idempotent?\n- POST\n* PUT\n- PATCH\n\nNext question...', help: 'Separate questions with a blank line. Mark the one right option with * and the others with -.' })}
        <button class="btn" type="submit">${a ? 'Save changes' : 'Create assessment'}</button></form></div>`;
  },
};

register({
  'assessment.save': (form) => submitForm(form, async (v) => {
    const body = { course_id: Number(v.course_id), title: v.title, kind: v.kind, pass_percent: Number(v.pass_percent), position: Number(v.position), active: v.active === 'true', questions: parseQuestions(v.questions) };
    if (form.dataset.id) await api.put(`/admin/assessments/${form.dataset.id}`, { body });
    else await api.post('/admin/assessments', { body });
    toast('Assessment saved', 'check');
    await navigate('/admin/assessments');
  }, { onError: (error) => toast(error.message, 'alert') }),
});

export const routes = [courses, courseEdit, batches, assessments, assessmentEdit];
